import PropTypes from 'prop-types';
import React, { useState, useEffect, Component } from 'react';

import { Select as SelectPreview, Row, Col, Label, FormGroup, InputGroup, InputNumberFormat } from 'twyne-new.ui';

import InfoTip from 'components/InfoTip';
import Input from 'components/form/Input';
import InputDecimal from 'components/form/InputDecimal';
import Select from 'components/form/Select';
import Switch from 'components/form/Switch';

const propTypes = {
  throttling: PropTypes.object,
  loadOverrideData: PropTypes.func,
  origin: PropTypes.string,
};

const numberProps = {
  decimalSeparator: ".",
  suffix: '%',
  prefix: '',
  decimalScale: 2,
  fixedDecimalScale: true,
}

export default class ScrubOptions extends Component {
  static useOverridableThrottling = (throttling, loadOverrideData) => {
    const { threshold: _threshold, initial_bucket, grouping } = throttling && throttling.options || {}
    const { percent, enabled, method } = throttling && throttling.margin_metric || {}
    const [defaultOptions, setDefaultOptions] = useState({})
    const [threshold, setThreshold] = useState(_threshold)

    useEffect(() => { loadData() }, [])

    const loadData = async () => {
      if (!loadOverrideData)
        return
      const { options } = await loadOverrideData()
      if (options)
        setDefaultOptions(options)
    }

    return {
      threshold: threshold && threshold * 100,
      thresholdRaw: threshold,
      grouping,
      initialBucket: initial_bucket || '',
      defaultThreshold: defaultOptions.threshold ? defaultOptions.threshold * 100 : '',
      defaultThresholdRaw: defaultOptions.threshold,
      defaultInitialBucket: defaultOptions.initial_bucket || '',
      defaultGrouping: defaultOptions.grouping,
      setThreshold,

      metricEnabled: enabled || false,
      metricPercent: percent || '',
      metricMethod: method || '',
    }
  }
  static DisableableComponent = ({ tag: Tag, ...props }) => <Tag {...props} />
  static ThrottlingOptions = ({ throttling, origin, disabled, loadOverrideData, payout }) => {
    const { threshold, grouping, initialBucket, defaultThreshold, defaultInitialBucket, defaultGrouping, defaultThresholdRaw, thresholdRaw, setThreshold } = ScrubOptions.useOverridableThrottling(throttling, loadOverrideData)

    const thresholdInput = disabled ? <InputNumberFormat {...numberProps} disabled className='form-control'
      value={defaultThreshold}
    /> : <InputDecimal {...numberProps} required name="throttling.options.threshold"
      value={`${threshold}`}
      onChange={value => value && setThreshold(value / 100)}
      containerProps={{ className: 'form-control p-0 border-0' }}
    />

    return <>
      {origin === THR_ORIGIN_SUBID ? null : <Col size='4'>
        <FormGroup>
          <Label>
            Grouping
            <InfoTip id='grouping' color='neutral' disabled>
              This configuration defines the granularity you want on every scrub bucket. Allocation the revenue will be counted on every allocation and for subid it will be provided on every subid sent.
            </InfoTip>
          </Label>
          {disabled ? <SelectPreview value={defaultGrouping} options={GROUPING_TYPES} disabled /> : <Select name="throttling.options.grouping" value={grouping} options={GROUPING_TYPES} required clearable={false} />}
        </FormGroup>
      </Col>}
      <Col size='6'>
        <FormGroup className='m-0'>
          <Label>
            Balance Threshold %
            <InfoTip id='threshold' color='neutral' disabled>
              This is the value of your bucket. The bucket is calculated the given number times the cost of your allocation. Ex: If your cost is $1 and the threshold is 100%, than your bucket size will be $2 USD. That means, it will only convert your traffic if the revenue generated on that allocation reach at least $2 in revenue.
            </InfoTip>
          </Label>
          {!payout ? thresholdInput : <>
            <div className='flex items-center'>
              {thresholdInput}
              <InputGroup className='flex items-center h-8 twy-subtle-neutral px-2'>
                <InputNumberFormat value={payout}
                  thousandSeparator={true}
                  displayType="text"
                  prefix='$'
                  noStyle
                />&nbsp;&#215;&nbsp;(1&nbsp;&#43;&nbsp;<InputNumberFormat value={disabled ? defaultThresholdRaw : thresholdRaw}
                  decimalSeparator="."
                  decimalScale={2}
                  fixedDecimalScale={true}
                  displayType="text"
                  noStyle
                />)&nbsp;&#61;&nbsp;<InputNumberFormat value={+payout + payout * (disabled ? defaultThresholdRaw : thresholdRaw) || 0}
                  thousandSeparator={true}
                  fixedDecimalScale={true}
                  decimalScale={2}
                  displayType="text"
                  prefix='$'
                  noStyle
                />
              </InputGroup>
            </div>
            <Label>
              <small>$Payout &#215; (1 &#43; threshold<b>%</b>) &#61; <b>$</b>threshold</small>
            </Label>
          </>}
        </FormGroup>
      </Col>
      <Col size={origin === THR_ORIGIN_SUBID ? 6 : 2}>
        <FormGroup class='w-full'>
          <Label>
            {origin === THR_ORIGIN_SUBID ? 'Initial Balance' : 'Initial B.'}
            <InfoTip id='initial_bucket' color='neutral' disabled side='left'>
              The inicial value is the first credit that will be given to an allocation when the bucket is created. This is used to force all the inicial conversions to fire the conversions. (warmup)
            </InfoTip>
          </Label>
          {disabled ? <InputNumberFormat value={defaultInitialBucket} disabled className='form-control w-full'
            thousandSeparator={true}
            prefix='$ '
          /> : <InputDecimal name="throttling.options.initial_bucket" value={initialBucket} required />
          }
        </FormGroup>
      </Col>
    </>
  }

  static ThrottlingGridResume = ({ subid, children, throttling: { throttling_method, options } }) => {
    switch (throttling_method) {
      case THR_METH_OVERRIDE:
        if (subid)
          return <span>Revenue Scrub: Allocation Settings</span>
        else
          return <span>Revenue Scrub: Global Settings</span>

      case THR_METH_BUCKET:
        const { threshold, initial_bucket } = options || {}
        return <span>
          <div className='twy-text-base-accent'>Revenue Scrub: Override</div>
          <span className='text-sm font-semibold twy-text-base-neutral uppercase'>
            Balance Threshold {Number(threshold * 100).toFixed(2)}% - Initial Balance ${Number(initial_bucket).toFixed(2)}
          </span>
        </span>

      case THR_METH_AUTO:
      default:
        return <span>
          <div className='twy-text-base-accent'>Manual Scrub</div>
          <span className='text-sm font-semibold twy-text-base-neutral uppercase'>
            {children}
          </span>
        </span>
    }
  }

  ThrottlingMethod = ({ method, metric, metricEnable, setMetricEnable, setMetric, metrics, metricPercent }) => {
    const { throttling, children, origin, loadOverrideData, payout } = this.props
    const { margin_metric: { minConverting } = {} } = throttling || {}

    switch (method) {
      case THR_METH_AUTO:
        return children

      case THR_METH_BUCKET:
      case THR_METH_OVERRIDE:
        return <>
          <Col size='8'>
            <Row>
              <ScrubOptions.ThrottlingOptions payout={payout} throttling={throttling} origin={origin} disabled={method === THR_METH_OVERRIDE} loadOverrideData={loadOverrideData} />
            </Row>
          </Col>
          <Col size="4">
            <FormGroup>
              <Label>
                Min. Metric Requirement
                <InfoTip id='minimum_metric_requirement' color='neutral' disabled>
                  Enable the minimum metric requirement calculation for subid. This is an additional verification to convert the sessions. If enabled we will use the metric margin to define the conversion.
                </InfoTip>
              </Label>
              <InputGroup>
                <Switch name="throttling.margin_metric.enabled" value={metricEnable} onChange={value => setMetricEnable(value)} />
              </InputGroup>
            </FormGroup>
          </Col>
          {metricEnable && <this.ThrottlingMetric
            metric={metric}
            setMetric={setMetric}
            metrics={metrics}
            metricPercent={metricPercent}
            minConverting={minConverting}
          />
          }
        </>

      default:
        return null
    }
  }

  ThrottlingMetric = ({ metric, setMetric, metrics, metricPercent, minConverting }) => {
    const [percent, setPercent] = useState(metricPercent)

    const changeMetric = (value) => {
      if (value > 500) {
        setPercent(500)
      } else {
        setPercent(value)
      }
    }

    return <>
      <Col size='4'>
        <FormGroup>
          <Label>
            Metric to Use
            <InfoTip id='margin_metric_method' color='neutral' disabled>
              The metric to use for the minimum margin requirement.
            </InfoTip>
          </Label>
          <Select
            required
            name="throttling.margin_metric.method"
            options={metrics}
            value={metric}
            setValue={setMetric}
            clearable={false} />
        </FormGroup>
      </Col>
      {metric === 'min-rps' || metric === 'min-rpu' ? <Col size='4'>
        <FormGroup className='m-0'>
          <Label className='flex items-center gap-1 capitalize'>
            {metric} conversion
            <InfoTip id='minimum-converting' color='neutral' disabled>
              The minimum dollar amount for converting the Subid.
            </InfoTip>
          </Label>
          <InputDecimal
            {...numberProps}
            thousandSeparator=","
            suffix=''
            prefix='$'
            fixedDecimalScale={false}
            required
            name="throttling.margin_metric.minConverting"
            value={minConverting}
            containerProps={{ className: 'form-control p-0 border-0' }}
          />
        </FormGroup>
      </Col> : <Col size='4'>
        <FormGroup className='m-0'>
          <Label className='flex items-center gap-1 capitalize'>
            {metric} Margin %
            <InfoTip id='margin_metric_percent' color='neutral' disabled>
              The percent of margin that must exists for sid1 for conversion to fire.
            </InfoTip>
          </Label>

          <InputDecimal
            {...numberProps}
            required
            name="throttling.margin_metric.percent"
            value={percent}
            onChange={value => changeMetric(value)}
            containerProps={{ className: 'form-control p-0 border-0' }}
          />
          <Label><small>Maximum margin is 500%</small></Label>
        </FormGroup>
      </Col>}

    </>
  }

  Content = () => {
    const { throttling, loadOverrideData, origin } = this.props
    const { metricPercent, metricEnabled, metricMethod } = ScrubOptions.useOverridableThrottling(throttling, loadOverrideData)

    const [method, setMethod] = useState(THR_METH_AUTO)
    const [methods, setMethods] = useState(METHOD_TYPES)

    const [metrics, setMetrics] = useState();
    const [metric, setMetric] = useState(metricMethod);

    const [metricEnable, setMetricEnable] = useState(metricEnabled);

    useEffect(() => {
      throttling && throttling.throttling_method && setMethod(throttling.throttling_method)
    }, [!!throttling && throttling.throttling_method])

    useEffect(() => { loadData() }, [])

    const loadData = async () => {
      let enableOverride = true

      if (loadOverrideData) {
        const { options } = await loadOverrideData()
        enableOverride = !options
      }

      setMethods(METHOD_TYPES.map(({ value, label }) => {
        label = origin === THR_ORIGIN_SUBID && value === THR_METH_OVERRIDE ? "Revenue Scrub: Allocation Settings" : label
        return {
          value, label, disabled: (enableOverride && value === THR_METH_OVERRIDE) || (enableOverride && value === THR_METH_BUCKET && origin === 'subid')
        }
      }))

      setMetrics(METRICS_TYPES)
    }

    return <>
      <Col size={origin === THR_ORIGIN_SUBID ? 4 : 3}>
        <FormGroup>
          <div className='flex items-center gap-1'>
            <Label>Throttling Method</Label>
            <InfoTip id='throttling_method' color='neutral' disabled>
              This configuration defines the default behavior for the traffic control.
            </InfoTip>
          </div>
          <Select name="throttling.throttling_method" options={methods} value={method} setValue={setMethod} required clearable={false} />
        </FormGroup>
      </Col>

      <this.ThrottlingMethod
        method={method}
        metric={metric}
        metricEnable={metricEnable}
        setMetricEnable={setMetricEnable}
        setMetric={setMetric}
        metrics={metrics}
        metricPercent={metricPercent}
      />
      {method !== THR_METH_BUCKET ? <Input name='throttling.options' value={undefined} type='hidden' /> : null}
    </>
  }

  render() {
    return <Col size="12">
      <Row>
        <this.Content />
      </Row>
    </Col>
  }
}

ScrubOptions.propTypes = propTypes
ScrubOptions.defaultProps = {
}

const THR_METH_AUTO = 'auto'
const THR_METH_OVERRIDE = 'override'
const THR_METH_BUCKET = 'bucket'

const THR_ORIGIN_SUBID = 'subid'
const THR_ORIGIN_ALLOCATION = 'allocation'
const THR_ORIGIN_GLOBAL = 'global'

const METHOD_TYPES = [
  {
    label: <span className='flex items-center gap-1'>Manual Scrub <InfoTip id={`option-${THR_METH_AUTO}`} color='neutral' disabled>
      This method will consider the scrub levels you setup at the allocation level.
    </InfoTip></span>,
    value: THR_METH_AUTO,
  },
  {
    label: <span className='flex items-center gap-1'>Revenue Scrub: Override <InfoTip id={`option-${THR_METH_BUCKET}`} color='neutral' disabled>
      This method will fire conversions only after the bucket size is fulfilled with the revenue configured at the threshold.
    </InfoTip></span>,
    value: THR_METH_BUCKET,
  },
  { label: 'Revenue Scrub: Global Settings', value: THR_METH_OVERRIDE },
]
const GROUPING_TYPES = [
  { label: 'Allocation', value: 'allocation' },
  { label: 'Allocation-Subid', value: 'allocation-subid' },
]

const METRICS_TYPES = [
  { label: 'RPS', value: 'rps' },
  { label: 'RPU', value: 'rpu' },
  { label: 'Minimum RPS Value', value: 'min-rps' },
  { label: 'Minimum RPU Value', value: 'min-rpu' },
]
